// -----------------------------------------------------------------------------
// ProductsPage — Vue liste des produits avec filtres, scanner, et +/-
// StockPro / VisualisPro
// -----------------------------------------------------------------------------

import { T } from "../../i18n/translations";
import { getColors } from "../../styles/theme";
import { daysUntil } from "../../utils/date";
import { Ic, ICONS } from "../Icons";

export default function ProductsPage({
  lang,
  dark,
  accent,
  isMobile,
  isStaff,
  products,
  filtered,
  totalValue,
  usedCats,
  usedZones,
  search,
  setSearch,
  catFilter,
  setCatFilter,
  zoneFilter,
  setZoneFilter,
  fcColor,
  fcLabel,
  onScan,
  onAddNew,
  onEdit,
  onDelete,
  onIncrement,
  onDecrement,
}) {
  const t = T[lang];
  const { textPrimary, textSecondary, borderColor } = getColors(dark);

  return (
    <div className="slide-in">
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:800, color:textPrimary, letterSpacing:"-0.5px" }}>{t.products}</h1>
          <p style={{ color:textSecondary, fontSize:12, marginTop:2 }}>
            {products.length} articles
            {!isStaff && <span> · <span style={{ color:"#10b981", fontFamily:"'JetBrains Mono',monospace" }}>€{totalValue.toFixed(2)}</span></span>}
          </p>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={onScan} style={{ padding:"9px 14px", borderRadius:9, background:`${accent}18`, color:accent, border:`1px solid ${accent}33`, fontSize:13, fontWeight:600, cursor:"pointer", display:"flex", alignItems:"center", gap:6 }}>
            <Ic d={ICONS.camera} size={15} color={accent} /> {t.scanBarcode}
          </button>
          <button className="glow-btn" onClick={onAddNew} style={{ padding:"9px 16px", borderRadius:9, background:`linear-gradient(135deg,${accent},#f7931e)`, color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer", border:"none", display:"flex", alignItems:"center", gap:6 }}>
            <Ic d={ICONS.plus} size={15} color="#fff" /> {t.addProduct}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display:"flex", gap:7, marginBottom:14, flexWrap:"wrap" }}>
        <div style={{ position:"relative", flex:"1", minWidth:170 }}>
          <div style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)" }}>
            <Ic d={ICONS.search} size={13} color="#888" />
          </div>
          <input className="fi" value={search} onChange={(e)=>setSearch(e.target.value)} placeholder={t.search} style={{ paddingLeft:30, fontSize:13 }} />
        </div>
        <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
          {["Tous", ...usedCats].map(c => (
            <button
              key={c}
              onClick={() => setCatFilter(c)}
              style={{ padding:"7px 11px", borderRadius:8, fontSize:11, fontWeight:600, background:catFilter===c?`${accent}20`:dark?"rgba(255,255,255,.04)":"rgba(0,0,0,.04)", color:catFilter===c?accent:textSecondary, border:`1px solid ${catFilter===c?accent+"40":borderColor}`, cursor:"pointer" }}
            >
              {c}
            </button>
          ))}
        </div>
        <div style={{ display:"flex", gap:5 }}>
          {["Tous", ...usedZones].map(z => (
            <button
              key={z}
              onClick={() => setZoneFilter(z)}
              style={{ padding:"7px 11px", borderRadius:8, fontSize:11, fontWeight:600, background:zoneFilter===z?"rgba(99,102,241,.2)":dark?"rgba(255,255,255,.04)":"rgba(0,0,0,.04)", color:zoneFilter===z?"#818cf8":textSecondary, border:`1px solid ${zoneFilter===z?"rgba(99,102,241,.4)":borderColor}`, cursor:"pointer" }}
            >
              {z}
            </button>
          ))}
        </div>
      </div>

      {/* Products list */}
      <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
        {filtered.map((p) => {
          const isLow = p.qty <= p.alert;
          const dlcD = daysUntil(p.dlc);
          const dlcBad = dlcD !== null && dlcD <= 3;
          return (
            <div key={p.id} className="card" style={{ padding:"11px 15px", borderColor:isLow?"rgba(239,68,68,.2)":dlcBad?"rgba(245,158,11,.2)":undefined }}>
              <div style={{ display:"flex", alignItems:isMobile?"flex-start":"center", gap:9, flexWrap:isMobile?"wrap":"nowrap" }}>
                <div style={{ width:7, height:7, borderRadius:"50%", background:isLow?"#ef4444":dlcBad?"#f59e0b":"#10b981", flexShrink:0, animation:(isLow||dlcBad)?"pulse 2s infinite":"none" }} />
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:4, marginBottom:4, flexWrap:"wrap" }}>
                    <span style={{ fontWeight:700, color:textPrimary, fontSize:13, display:"block", marginBottom:4 }}>{p.name}</span>
                    <span className="tag" style={{ background:`${accent}15`, color:accent, fontSize:10 }}>{p.cat}</span>
                    {p.zone && <span className="tag" style={{ background:"rgba(99,102,241,.12)", color:"#818cf8", fontSize:10 }}>{p.zone}</span>}
                    {isLow && <span className="tag" style={{ background:"rgba(239,68,68,.12)", color:"#f87171", fontSize:10 }}>⚠️ Bas</span>}
                    {dlcBad && <span className="tag" style={{ background:"rgba(245,158,11,.12)", color:"#fbbf24", fontSize:10 }}>{dlcD<=0?t.dlcExpired:`DLC ${dlcD}j`}</span>}
                  </div>
                  <div style={{ height:3, background:dark?"rgba(255,255,255,.05)":"rgba(0,0,0,.06)", borderRadius:2, width:"100px" }}>
                    <div style={{ height:"100%", width:`${Math.min(100,(p.qty/p.alert)*100)}%`, background:isLow?"#ef4444":"#10b981", borderRadius:2 }} />
                  </div>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:6, marginLeft:isMobile?"auto":"0" }}>
                  {isMobile ? (
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                      <button className="qbtn" onClick={()=>onIncrement(p)} style={{ background:"rgba(16,185,129,.1)", color:"#10b981" }}>+</button>
                      <div style={{ textAlign:"center", minWidth:40 }}>
                        <div style={{ fontSize:16, fontWeight:800, color:isLow?"#f87171":textPrimary, fontFamily:"'JetBrains Mono',monospace" }}>{p.qty}</div>
                        <div style={{ fontSize:10, color:textSecondary }}>{p.unit}</div>
                      </div>
                      <button className="qbtn" onClick={()=>onDecrement(p)} style={{ background:"rgba(239,68,68,.1)", color:"#f87171" }}>-</button>
                    </div>
                  ) : (
                    <>
                      <button className="qbtn" onClick={()=>onDecrement(p)} style={{ background:"rgba(239,68,68,.1)", color:"#f87171" }}>-</button>
                      <div style={{ textAlign:"center", minWidth:50 }}>
                        <div style={{ fontSize:16, fontWeight:800, color:isLow?"#f87171":textPrimary, fontFamily:"'JetBrains Mono',monospace" }}>{p.qty}</div>
                        <div style={{ fontSize:10, color:textSecondary }}>{p.unit}</div>
                      </div>
                      <button className="qbtn" onClick={()=>onIncrement(p)} style={{ background:"rgba(16,185,129,.1)", color:"#10b981" }}>+</button>
                    </>
                  )}
                </div>
                {!isStaff && (
                  <div style={{ textAlign:"right", minWidth:58 }}>
                    <div style={{ fontSize:11, color:"#10b981", fontFamily:"'JetBrains Mono',monospace" }}>€{((p.qty||0)*(p.price||0)).toFixed(2)}</div>
                    <div style={{ fontSize:10, color:textSecondary }}>€{p.price||0}/{p.unit}</div>
                    {p.sellPrice > 0 && p.price > 0 && (() => {
                      const fc = Math.round((p.price/p.sellPrice)*100);
                      return <div style={{ fontSize:10, fontWeight:700, color:fcColor(fc), fontFamily:"'JetBrains Mono',monospace", marginTop:2 }}>{fcLabel(fc)} FC {fc}%</div>;
                    })()}
                  </div>
                )}
                <div style={{ display:"flex", gap:4 }}>
                  <button onClick={()=>onEdit(p)} style={{ width:28, height:28, borderRadius:7, background:dark?"rgba(255,255,255,.05)":"rgba(0,0,0,.05)", color:textSecondary, display:"flex", alignItems:"center", justifyContent:"center", border:"none", cursor:"pointer" }}>
                    <Ic d={ICONS.edit} size={12} />
                  </button>
                  <button onClick={()=>onDelete(p)} style={{ width:28, height:28, borderRadius:7, background:"rgba(239,68,68,.08)", color:"#f87171", display:"flex", alignItems:"center", justifyContent:"center", border:"none", cursor:"pointer" }}>
                    <Ic d={ICONS.trash} size={12} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        {!filtered.length && (
          <div style={{ textAlign:"center", padding:"40px", color:textSecondary }}>{t.noProducts}</div>
        )}
      </div>
    </div>
  );
}
